import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/logo.png";

const NAV_LINKS = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#tools", label: "Tools" },
    { href: "#pricing", label: "Pricing" },
];

export default function Navigation() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-black/85 backdrop-blur-xl border-b border-gold/20" : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={LOGO_URL}
                        alt="Nicholas Kraus Consulting"
                        className="h-12 w-12 object-contain"
                    />
                    <div className="hidden sm:block leading-tight">
                        <div className="font-display text-base tracking-[0.18em] text-cream uppercase">
                            Nicholas Kraus
                        </div>
                        <div className="text-[10px] tracking-[0.4em] text-gold uppercase">
                            Consulting
                        </div>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-sm tracking-wide text-cream/70 hover:text-gold transition-colors uppercase font-medium"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Button
                        data-testid="nav-contact-cta"
                        asChild
                        className="hidden md:inline-flex bg-gold text-ink hover:bg-gold-light rounded-none px-6 h-11 font-semibold tracking-wider uppercase text-xs"
                    >
                        <a href="#contact">Work With Us</a>
                    </Button>
                    <button
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden text-cream p-2"
                        aria-label="Toggle navigation"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-gold/15 bg-black/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                                className="text-cream/80 uppercase tracking-wider text-sm"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-contact"
                            className="inline-block bg-gold text-ink px-5 py-3 uppercase tracking-wider text-sm font-semibold"
                        >
                            Work With Us
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
